import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PART_FILTER_CATEGORIES, PART_FILTER_FIELD_DEFINITIONS } from './partFilterFields';
import { EMPTY_FILTERS, SERIALISED_OPTIONS } from './partListFilters';
import { PartListHeader } from './PartListHeader';
import { usePartFilterListOptions } from './usePartFilterListOptions';
import { usePartListData } from './usePartListData';
import { usePartReports } from './usePartReports';
import { ActionPermissions, ListType } from '../../../@types';
import { mapPartFilterOperatorForWire } from '../../../api/parts';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { FilterBar, FilterState } from '../../../components/molecules/FilterBar';
import { AutoRefresh, TablePagination } from '../../../components/molecules/Pagination';
import { ReportTabs, SaveReportDialog } from '../../../components/molecules/ReportTabs';
import { PartTable } from '../../../components/organisms/PartTable';
import { SortModel } from '../../../components/organisms/ServiceTable';
import { ColumnSelection } from '../../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../../configs/common';
import { SavedReportTypeList } from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import { TabHandler } from '../../../handlers/TabHandler';
import { useClientVisibleFieldGuard } from '../../../hooks/useClientVisibleFieldGuard';
import { useIsClient } from '../../../hooks/useIsClient';
import { useListExport } from '../../../hooks/useListExport';
import { usePinnedReports } from '../../../hooks/usePinnedReports';
import { useReportsModal } from '../../../hooks/useReportsModal';
import { useSessionColumnPersist } from '../../../hooks/useSessionColumnPersist';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { clearSessionColumnState } from '../../../utils/sessionColumnState';
import { ReportsModal } from '../../Reports/MyReportsList/ReportsModal';

const PartListInner: React.FC = () => {
  const theme = useTheme();
  const permissionsQuery = useGetUserSystemPermissions();
  const permissions = permissionsQuery.data;
  // useIsClient is fail-closed: returns true while the visibility query is
  // still loading or has errored, so client-only restrictions apply
  // conservatively when the user's visibility state isn't yet known.
  const isClient = useIsClient();
  const { pinnedCodes, setPinnedCodes, defaultTabIndex, reorderTabs } =
    usePinnedReports('PartList');

  const {
    data,
    isFetching,
    page,
    pageSize,
    total,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData,
    filterModel,
    multiSearchQuery,
    conditionOptions,
    contractOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    reorderColumns,
    resetColumnsToDefault,
    restoreDefaultColumns,
    setColumnWidth,
    refetch,
  } = usePartListData();

  const filterListOptions = usePartFilterListOptions();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [filterBarKey, setFilterBarKey] = useState(0);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const [columnSelectionOpen, setColumnSelectionOpen] = useState(false);

  const {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
    setSaveDialogOpen,
  } = usePartReports({
    filterModel,
    pageSize,
    sortModel,
    columns,
    columnSelectionData,
    applyFilters,
    applyReportConfig,
    resetColumnsToDefault,
    setFilters,
    setFilterBarKey,
  });

  const { recentCodes, isReportsModalOpen, openReportsModal, closeReportsModal } = useReportsModal(
    SavedReportTypeList.PartList,
    activeReport
  );

  // Persist the default-view column layout to the browser session — debounced so
  // a resize drag writes once on release, not on every frame. Skipped while a
  // saved report is active — that layout belongs to the report, not the session.
  useSessionColumnPersist('PartList', columns, !activeReport);

  // Default tab context-menu actions.

  const handleRevertToDefaultReport = useCallback(
    () => handleReportChange(null),
    [handleReportChange]
  );
  const handleRevertToSystemDefaults = useCallback(() => {
    const current = savedReports.find((r) => r.isDefault);
    if (current) handleSetDefault({ ...current, isDefault: false });
    clearSessionColumnState('PartList');
    resetColumnsToDefault();
    setFilters(EMPTY_FILTERS);
    applyFilters(EMPTY_FILTERS);
  }, [savedReports, handleSetDefault, resetColumnsToDefault, setFilters, applyFilters]);

  TabHandler(`Parts | ${PLATFORM_NAME}`);

  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => refetch(), autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, refetch]);

  const handleSearch = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      applyFilters(newFilters);
    },
    [applyFilters]
  );

  // Lock client/customer users into filtering and sorting by the columns the
  // curated view actually exposes. Non-clients pass through unchanged.
  const visibleFields = useMemo(
    () => columns.filter((c) => c.visible).map((c) => c.key),
    [columns]
  );
  const { restrictedFieldDefinitions, guardSortChange } = useClientVisibleFieldGuard(
    PART_FILTER_FIELD_DEFINITIONS,
    visibleFields
  );

  const handleSortChange = useMemo(
    () => guardSortChange<SortModel>((model) => setSortModel(model)),
    [guardSortChange, setSortModel]
  );

  const { exportAnchor, setExportAnchor, handleExport } = useListExport({
    fileName: ListType.PartList,
    columns,
    columnSelectionData,
    filterModel,
    // Apply the same numeric-column operator fix the list query uses, so
    // an exported list with an Asset/Part Type/Node ID "equals" filter
    // matches what's on screen instead of erroring on the BFF
    // (see mapPartFilterOperatorForWire).
    mapFilterItems: (items) =>
      items.map((item) => ({
        ...item,
        operator: mapPartFilterOperatorForWire(item.field, item.operator) ?? item.operator,
      })),
    rowCount: total,
    pageSize,
    sorting: [{ field: sortModel.field, sort: sortModel.direction }],
    // The parts list query sends this (see getAllParts), so the export has to
    // as well — without it a searched list exported every part.
    multiSearchQuery,
  });

  const hasSearchPermission = isUserHasPermission(ActionPermissions.Part_Search, permissions);

  return (
    <>
      <PartListHeader
        total={total}
        permissions={permissions}
        exportAnchor={exportAnchor}
        onExportClick={(e) => setExportAnchor(e.currentTarget)}
        onExportClose={() => setExportAnchor(null)}
        onExportCsv={() => handleExport('csv')}
        onExportExcel={() => handleExport('xlsx')}
        onColumnsClick={isClient ? undefined : () => setColumnSelectionOpen(true)}
        onReportsClick={openReportsModal}
      />
      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ReportTabs
            activeReport={activeReport}
            reports={savedReports}
            onReportChange={handleReportChange}
            recentCodes={recentCodes}
            onSave={() => setSaveDialogOpen(true)}
            defaultTabLabel={defaultTabLabel}
            onSetDefault={handleSetDefault}
            pinnedCodes={pinnedCodes}
            onPinnedCodesChange={setPinnedCodes}
            defaultTabIndex={defaultTabIndex}
            onReorderTabs={reorderTabs}
            onRevertToSystemDefaults={handleRevertToSystemDefaults}
            onRevertToDefaultReport={handleRevertToDefaultReport}
          />
          <SaveReportDialog
            open={saveDialogOpen}
            onClose={() => setSaveDialogOpen(false)}
            onSaveNew={handleSaveNewReport}
            onOverwrite={handleOverwriteReport}
            activeReportName={activeReportName}
          />

          {hasSearchPermission ? (
            <>
              <FilterBar
                key={filterBarKey}
                onSearch={handleSearch}
                statuses={contractOptions}
                jobTypes={conditionOptions}
                contracts={SERIALISED_OPTIONS}
                initialFilters={filters}
                labels={{
                  statusesDropdown: 'Contract',
                  jobTypesDropdown: 'Part Condition',
                  contractsDropdown: 'Serialised',
                  extraTextPlaceholder: 'Part Type',
                  hideIdField: true,
                  searchPlaceholder: 'Serial search',
                  searchButtonAriaLabel: 'Search parts',
                }}
                fieldConfig={{
                  fieldDefinitions: restrictedFieldDefinitions ?? PART_FILTER_FIELD_DEFINITIONS,
                  categories: PART_FILTER_CATEGORIES,
                  listOptions: filterListOptions,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '20px',
                  py: '3px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  {total > 0 && (
                    <span>
                      Showing {page * pageSize + 1}&ndash;
                      {Math.min((page + 1) * pageSize, total)} of {total.toLocaleString()}
                    </span>
                  )}
                  {fetchDurationMs !== null && (
                    <span>Took {(fetchDurationMs / 1000).toFixed(2)}s</span>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  <label htmlFor="parts-rows-per-page-select">Rows:</label>
                  <Select
                    id="parts-rows-per-page-select"
                    size="small"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(0);
                    }}
                    aria-label="Rows per page"
                    sx={{
                      fontSize: '12px',
                      height: 26,
                      '& .MuiSelect-select': { py: 0.25, px: 1 },
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
              <PartTable
                data={data}
                columns={columns}
                sortModel={sortModel}
                onSortChange={handleSortChange}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onColumnReorder={reorderColumns}
                onColumnResize={setColumnWidth}
                loading={isFetching}
                permissions={permissions}
                isClientCustomer={isClient}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '20px',
                  pb: 2,
                }}
              >
                <Box sx={{ width: 160 }} />
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
                <AutoRefresh interval={autoRefreshInterval} onChange={setAutoRefreshInterval} />
              </Box>
            </>
          ) : (
            <Typography sx={{ mt: 4, px: '20px', color: theme.palette.text.secondary }}>
              You do not have permission to view parts.
            </Typography>
          )}

          {!isClient && (
            <ColumnSelection
              open={columnSelectionOpen}
              title="Select Columns"
              value={columnSelectionData}
              onChange={updateColumnSelection}
              onClose={() => setColumnSelectionOpen(false)}
              onReset={restoreDefaultColumns}
            />
          )}
          <ReportsModal
            open={isReportsModalOpen}
            onClose={closeReportsModal}
            pageName={SavedReportTypeList.PartList}
            onSelectReport={handleReportChange}
          />
        </Box>
      </PageContainer>
    </>
  );
};

export const PartList: React.FC = () => <PartListInner />;
